import React from "react";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import { ConfirmPopUp } from "../../../../../components/PopUp";
import type { ConfirmDetail } from "../../../../../components/PopUp/confirmShared/types";
import {
  isPricedValuation,
  valuationMessageFor,
} from "../../../../../services/valuation";
import { formatCurrency } from "../../../../../utils";
import { presentedValueOf } from "../../../utils";
import { StatusChangeModalProps } from "./types";

export const StatusChangeModal: React.FC<StatusChangeModalProps> = ({
  isOpen,
  investment,
  onClose,
  onConfirm,
}) => {
  const { name, institution, isActive, contributionsCount } = investment;
  const presentedValue = presentedValueOf(investment);
  const isPriced = isPricedValuation({
    value: presentedValue,
    valuation: investment.valuation,
  });

  const contributionsLabel = `${contributionsCount.toLocaleString()} contribution${
    contributionsCount === 1 ? "" : "s"
  }`;
  const subtitle = [institution, contributionsLabel]
    .filter(Boolean)
    .join(" · ");

  const detail: ConfirmDetail = {
    icon: <ShowChartRoundedIcon />,
    title: name,
    subtitle,
    amount:
      isPriced && presentedValue !== null
        ? formatCurrency(presentedValue)
        : valuationMessageFor(investment.valuation),
    amountVariant:
      presentedValue !== null && presentedValue < 0 ? "expense" : "neutral",
  };

  const deactivateText =
    "It will be hidden from your active investments and excluded from totals by default. Transactions stay intact, and you can reactivate any time.";
  const reactivateText =
    "It will return to your active investments list and be included in totals again. You can deactivate it later at any time.";

  const title = isActive ? `Deactivate ${name}?` : `Reactivate ${name}?`;
  const text = isActive ? deactivateText : reactivateText;
  const confirmLabel = isActive
    ? "Deactivate investment"
    : "Reactivate investment";
  const loadingLabel = isActive ? "Deactivating…" : "Reactivating…";
  const icon = isActive ? (
    <PauseCircleOutlineRoundedIcon />
  ) : (
    <PlayCircleOutlineRoundedIcon />
  );

  return (
    <ConfirmPopUp
      isOpen={isOpen}
      setIsOpen={onClose}
      tone="warning"
      icon={icon}
      title={title}
      text={text}
      details={detail}
      confirmLabel={confirmLabel}
      loadingLabel={loadingLabel}
      onConfirm={onConfirm}
    />
  );
};
